import { usePrevious } from '@lemonade-hq/cdk';
import type { LevelOneProgressItem } from '@lemonade-hq/uniclient-chat';
import { Flex, spacing, useDelayedValue } from '@lemonade-hq/uniclient-theme';
import { AnimatePresence, motion } from 'motion/react';
import { forwardRef } from 'react';
import { getAnimationState } from '../animationHelpers';
import { textVariants } from '../animations';
import { DELAY_PREVIOUS_ITEM_DURATION, TEXT_LINE_HEIGHT } from '../constants';
import { useCurrentIndex } from '../hooks';
import type { ActionProps, BaseProgressItemProps } from '../types';
import { ItemContainer } from './common/ItemContainer';
import { ItemContentWrapper } from './common/ItemContentWrapper';
import { ItemTitle } from './common/ItemTitle';
import { OuterDot } from './Dots';
import { Line } from './Line';
import * as styles from './Line.css';
import { ProgressSubItemsWrapper } from './ProgressSubItemsWrapper';

export interface ProgressItemProps extends BaseProgressItemProps {
  readonly item: LevelOneProgressItem;
  readonly status?: LevelOneProgressItem['status'];
  readonly isLast: boolean;
  readonly currentIndex: number;
  readonly currentSubItemId?: string;
  readonly currentSubSubItemId?: string;
  readonly onAction?: ActionProps['onAction'];
  readonly isFirstRender: boolean;
}

export const ProgressItem = forwardRef<HTMLDivElement, ProgressItemProps>(
  (
    {
      item,
      completed,
      current,
      isLast,
      currentIndex,
      currentSubItemId,
      currentSubSubItemId,
      onAction,
      status,
      isFirstRender,
    },
    _,
  ) => {
    // Items
    const previousItemIndex = usePrevious(currentIndex, currentIndex);
    const delayedPreviousItemIndex = useDelayedValue(
      previousItemIndex,
      previousItemIndex,
      DELAY_PREVIOUS_ITEM_DURATION,
    );
    const isItemsBackwards = delayedPreviousItemIndex > currentIndex;

    // Sub Items
    const subItems = item.subItems;
    const currentSubItemIndex = useCurrentIndex(currentSubItemId, subItems);
    const previousSubItemIndex = usePrevious(currentSubItemIndex, currentSubItemIndex);
    const delayedPreviousSubItemIndex = useDelayedValue(
      previousSubItemIndex,
      previousSubItemIndex,
      DELAY_PREVIOUS_ITEM_DURATION,
    );
    const isSubItemsBackwards =
      (currentSubItemIndex === -1 && delayedPreviousSubItemIndex !== -1) ||
      delayedPreviousSubItemIndex > currentSubItemIndex;
    const hasSubItems = subItems != null;
    const numberOfSubItems = subItems?.length ?? 0;
    const isLastSubItem = hasSubItems && currentSubItemIndex === numberOfSubItems - 1;
    const showSubItems = current && !completed && subItems != null && currentSubItemId != null;

    // Sub Sub Items
    const subSubItems = subItems?.[currentSubItemIndex]?.subItems;
    const currentSubSubItemIndex = useCurrentIndex(currentSubSubItemId, subSubItems);
    const previousSubSubItemIndex = usePrevious(currentSubSubItemIndex, currentSubSubItemIndex);
    const delayedPreviousSubSubItemIndex = useDelayedValue(
      previousSubSubItemIndex,
      previousSubSubItemIndex,
      DELAY_PREVIOUS_ITEM_DURATION,
    );
    const isSubSubItemsBackwards =
      (currentSubSubItemIndex === -1 && delayedPreviousSubSubItemIndex !== -1) ||
      delayedPreviousSubSubItemIndex > currentSubSubItemIndex;
    const hasSubSubItems = subSubItems != null;
    const numberOfSubSubItems = subItems?.[currentSubItemIndex]?.subItems?.length ?? 0;
    const isLastSubSubItem = hasSubSubItems && currentSubSubItemIndex === numberOfSubSubItems - 1;

    const isBackwards = isItemsBackwards || ((isSubItemsBackwards || isSubSubItemsBackwards) && !isLastSubItem);

    return (
      <ItemContainer>
        <Flex alignItems="flex-start" cursor="default" gap={spacing.s12}>
          <Flex
            flexDirection="column"
            height="1lh"
            justifyContent="center"
            style={{ lineHeight: `${TEXT_LINE_HEIGHT}em` }}
          >
            <OuterDot
              action={item.action}
              completed={completed}
              current={current}
              disabled={item.disabled}
              isBackwards={isBackwards}
              onAction={onAction}
              status={status}
            />
            {!isLast && (
              <div className={styles.line}>
                <Line
                  completed={completed}
                  currentSubItemIndex={showSubItems ? currentSubItemIndex : -1}
                  currentSubSubItemIndex={currentSubSubItemIndex}
                  isBackwards={isBackwards}
                  isLastItem={isLastSubItem && isLastSubSubItem}
                  level={currentSubSubItemId != null ? 3 : 2}
                  numOfSubItems={showSubItems ? numberOfSubItems : 0}
                  numOfSubSubItems={numberOfSubSubItems}
                />
              </div>
            )}
          </Flex>

          <ItemContentWrapper isFirstRender={isFirstRender}>
            <motion.div
              animate={getAnimationState(completed, current)}
              initial={false}
              layout
              variants={textVariants({ level: 1, isExpanding: false, isBackwards })}
            >
              <ItemTitle
                action={item.action}
                completed={completed}
                current={current}
                isBackwards={false}
                isDisabled={item.disabled}
                isExpanding={false}
                level={1}
                onAction={onAction}
                title={item.title}
                type="h6"
              />
            </motion.div>
            <AnimatePresence initial={false} mode="popLayout">
              {showSubItems && (
                <ProgressSubItemsWrapper
                  currentSubItemIndex={currentSubItemIndex}
                  currentSubSubItemIndex={currentSubSubItemIndex}
                  isBackwards={isBackwards}
                  isFirstRender={isFirstRender}
                  items={subItems}
                />
              )}
            </AnimatePresence>
          </ItemContentWrapper>
        </Flex>
      </ItemContainer>
    );
  },
);
